import { useEffect, createContext, useContext } from 'react'
import { IonLoading } from '@ionic/react'
import { FirebaseMessaging } from '@capacitor-firebase/messaging'

import { usePushToken } from '../utils/use-push-token'
import { updateInstallation } from './api'

import type { Children } from '../types'
import { useQuery, useQueryClient } from '@tanstack/react-query'

const pushNotificationContext = createContext<{
  token: string | null
  status?: PermissionState | 'error'
} | null>(null)

function PushNotificationContextProvider({ children }: Children) {
  const statusQuery = useQuery({
    queryKey: ['permissionstatus'],
    queryFn: async () => {
      return FirebaseMessaging.checkPermissions()
    },
    refetchOnWindowFocus: false,
  })
  const tokenQuery = usePushToken({
    enabled: statusQuery.data?.receive === 'granted',
  })
  const loading = statusQuery.isLoading || tokenQuery.isInitialLoading
  const error = statusQuery.error || tokenQuery.error

  useEffect(() => {
    if (!tokenQuery.data) return

    updateInstallation(tokenQuery.data)
  }, [tokenQuery.data])

  if (loading) {
    return <IonLoading isOpen message="Loading queries" />
  }

  if (error) {
    return (
      <pushNotificationContext.Provider
        value={{ token: null, status: 'error' }}
      >
        {children}
      </pushNotificationContext.Provider>
    )
  }

  if (statusQuery.data?.receive === 'denied') {
    return (
      <pushNotificationContext.Provider
        value={{ token: null, status: 'denied' }}
      >
        {children}
      </pushNotificationContext.Provider>
    )
  }

  if (statusQuery.data?.receive === 'granted') {
    if (tokenQuery.status === 'success') {
      return (
        <pushNotificationContext.Provider
          value={{ status: 'granted', token: tokenQuery.data }}
        >
          {children}
        </pushNotificationContext.Provider>
      )
    }
    if (tokenQuery.status === 'error') {
      return <p>Error:</p>
    }

    return <IonLoading isOpen message="Loading PN Context..." />
  }

  return (
    <pushNotificationContext.Provider value={{ status: 'prompt', token: null }}>
      {children}
    </pushNotificationContext.Provider>
  )
}

function usePushNotificationToken() {
  const pushToken = useContext(pushNotificationContext)

  if (!pushToken) {
    throw new Error('PushNotificationContext: No value provided')
  }

  return { pushToken: pushToken.token }
}

export function usePushStatus() {
  const pushToken = useContext(pushNotificationContext)
  const queryClient = useQueryClient()

  function requestPermission() {
    FirebaseMessaging.requestPermissions().then(() =>
      queryClient.invalidateQueries({
        queryKey: ['permissionstatus'],
      })
    )
  }

  if (!pushToken) {
    throw new Error('PushNotificationContext: No value provided')
  }

  return { requestPermission, status: pushToken.status }
}

export { PushNotificationContextProvider, usePushNotificationToken }
